import * as THREE from "three";
import { WallType } from "../types/floorplannerTypes";
import { getArcTangentAtEndpoint, getQuadraticBezierPoint } from "./arcUtils";

export interface ImaginaryLineSegment {
  start: THREE.Vector2;
  end: THREE.Vector2;
  direction: THREE.Vector2;
  normal: THREE.Vector2;
  length: number;
}

export interface WallImaginaryLines {
  left: ImaginaryLineSegment;
  right: ImaginaryLineSegment;
}

export interface ImaginaryLineIntersection {
  point: THREE.Vector2;
  sourceT: number;
  targetT: number;
}

const DEFAULT_EXTENSION = 5;

const clonePoint = (point: THREE.Vector2 | { x: number; y: number }) =>
  (point as THREE.Vector2).clone ? (point as THREE.Vector2).clone() : new THREE.Vector2(point.x, point.y);

const getWallDirectionForEndpoint = (
  wall: WallType,
  endpoint?: "start" | "end",
): THREE.Vector2 => {
  if (endpoint && wall.lineForm === "arc" && wall.controlPoint) {
    return getArcTangentAtEndpoint(wall, endpoint).clone().normalize();
  }

  const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = direction.length();
  return length < 1e-6 ? new THREE.Vector2(1, 0) : direction.divideScalar(length);
};

/**
 * Returns the overall chord direction of a wall (from start to end),
 * irrespective of arc curvature. Used for colinearity detection where the
 * local tangent of an arc can misleadingly be near-parallel to another wall
 * while the arc's overall geometry is not.
 */
const getWallChordDirection = (wall: WallType): THREE.Vector2 => {
  const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = direction.length();
  return length < 1e-6 ? new THREE.Vector2(1, 0) : direction.divideScalar(length);
};

/**
 * Check if two walls are nearly colinear (parallel or anti-parallel).
 * Uses the cross product of their direction vectors.
 * 
 * @param sourceWall - First wall to compare
 * @param targetWall - Second wall to compare
 * @param tolerance - Tolerance for colinearity (default: ~11 degrees as 0.2 radians)
 * @returns true if walls are nearly colinear
 */
export const areWallsNearlyColinear = (
  sourceWall: WallType,
  targetWall: WallType,
  tolerance: number = 0.2
): boolean => {
  const sourceDir = new THREE.Vector2()
    .subVectors(sourceWall.end, sourceWall.start);
  const targetDir = new THREE.Vector2()
    .subVectors(targetWall.end, targetWall.start);

  const sourceLen = sourceDir.length();
  const targetLen = targetDir.length();

  if (sourceLen < 1e-6 || targetLen < 1e-6) {
    return false;
  }

  sourceDir.divideScalar(sourceLen);
  targetDir.divideScalar(targetLen);

  // Cross product magnitude tells us how parallel the walls are
  // Near 0 means parallel, near ±1 means perpendicular
  const cross = Math.abs(sourceDir.x * targetDir.y - sourceDir.y * targetDir.x);

  // sin(tolerance) ≈ 0.198 for 0.2 radians (~11 degrees)
  return cross < Math.sin(tolerance);
};

/**
 * Calculate extended parallel lines for a wall that can be used for robust
 * intersection testing regardless of trimming order.
 *
 * @param wall - The wall to calculate lines for
 * @param fallbackWidth - Fallback width if wall.wallWidth is not set
 * @param extension - How far to extend the lines beyond the wall endpoints
 * @param includeLineWeight - If true, calculates lines at OUTLINE edges (wallWidth/2 + lineWeight/2), otherwise at FILL edges (wallWidth/2)
 */
export const calculateWallImaginaryLines = (
  wall: WallType,
  fallbackWidth: number = 0.1,
  extension: number = DEFAULT_EXTENSION,
  includeLineWeight: boolean = false,
  endpoint?: "start" | "end",
): WallImaginaryLines => {
  const start = clonePoint(wall.start);
  const end = clonePoint(wall.end);
  const wallVector = new THREE.Vector2().subVectors(end, start);
  const wallLength = wallVector.length();

  const direction = getWallDirectionForEndpoint(wall, endpoint);

  const perpendicular = new THREE.Vector2(-direction.y, direction.x);
  let halfWidth = (wall.wallWidth ?? fallbackWidth) / 2;

  // If includeLineWeight is true, extend to the outline edge
  if (includeLineWeight) {
    // Deferred to avoid the floorplannerStore -> imaginaryLineHelpers cycle.
    const { floorplannerStore } = require("../store/floorplannerStore");
    const lineWeight = floorplannerStore.convertLineWeightToWorld(wall.lineWeight || floorplannerStore.wallLineWeight);
    halfWidth += lineWeight / 2;
  }

  // Extend lines from the local endpoint for endpoint joints, otherwise from the
  // wall midpoint for body/middle connections.
  const center = endpoint
    ? endpoint === "start" ? start : end
    : new THREE.Vector2().addVectors(start, end).multiplyScalar(0.5);
  const halfSpan = endpoint
    ? wallLength + extension
    : wallLength / 2 + extension;
  const spanOffset = direction.clone().multiplyScalar(halfSpan);

  const leftStart = center.clone().sub(spanOffset).addScaledVector(perpendicular, halfWidth);
  const leftEnd = center.clone().add(spanOffset).addScaledVector(perpendicular, halfWidth);
  const rightStart = center.clone().sub(spanOffset).addScaledVector(perpendicular, -halfWidth);
  const rightEnd = center.clone().add(spanOffset).addScaledVector(perpendicular, -halfWidth);

  const length = halfSpan * 2;

  return {
    left: {
      start: leftStart,
      end: leftEnd,
      direction: direction.clone(),
      normal: perpendicular.clone(),
      length,
    },
    right: {
      start: rightStart,
      end: rightEnd,
      direction: direction.clone(),
      normal: perpendicular.clone().multiplyScalar(-1),
      length,
    },
  };
};

/**
 * Intersect two imaginary line segments treating them as infinite lines.
 */
export const intersectImaginaryLines = (
  source: ImaginaryLineSegment,
  target: ImaginaryLineSegment,
  epsilon: number = 1e-10
): ImaginaryLineIntersection | null => {
  const r = new THREE.Vector2().subVectors(source.end, source.start);
  const s = new THREE.Vector2().subVectors(target.end, target.start);
  const cross = r.x * s.y - r.y * s.x;

  if (Math.abs(cross) < epsilon) {
    return null;
  }

  const qp = new THREE.Vector2().subVectors(target.start, source.start);
  const t = (qp.x * s.y - qp.y * s.x) / cross;
  const u = (qp.x * r.y - qp.y * r.x) / cross;
  const point = source.start.clone().addScaledVector(r, t);

  return { point, sourceT: t, targetT: u };
};

export interface EndpointImaginaryIntersections {
  left: THREE.Vector2;
  right: THREE.Vector2;
}

/**
 * Sample the two outline polylines of an arc wall (left-of-tangent and right-of-tangent)
 * using the same tangent-based perpendicular scheme as calculateLineVertices. This lets
 * us intersect straight imaginary lines with the arc's ACTUAL visible outline instead of
 * the misleading chord-based approximation used for straight walls.
 *
 * Returned polylines are ordered along t ∈ [0, 1] from wall.start to wall.end.
 */
const sampleArcOutlinePolylines = (
  wall: WallType,
  halfWidth: number,
  segments: number = 40,
): { left: THREE.Vector2[]; right: THREE.Vector2[] } | null => {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    return null;
  }

  const p0 = wall.start as THREE.Vector2;
  const p1 = wall.controlPoint as THREE.Vector2;
  const p2 = wall.end as THREE.Vector2;

  const chord = new THREE.Vector2().subVectors(p2, p0);
  const chordPerp = chord.lengthSq() > 1e-12
    ? new THREE.Vector2(-chord.y, chord.x).normalize()
    : new THREE.Vector2(0, 1);

  const startTan = new THREE.Vector2(p1.x - p0.x, p1.y - p0.y);
  const endTan = new THREE.Vector2(p2.x - p1.x, p2.y - p1.y);
  const startPerp = startTan.lengthSq() > 1e-12
    ? new THREE.Vector2(-startTan.y, startTan.x).normalize()
    : chordPerp.clone();
  const endPerp = endTan.lengthSq() > 1e-12
    ? new THREE.Vector2(-endTan.y, endTan.x).normalize()
    : chordPerp.clone();

  const left: THREE.Vector2[] = [];
  const right: THREE.Vector2[] = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const point = getQuadraticBezierPoint(t, p0, p1, p2);

    let perp: THREE.Vector2;
    if (i === 0) {
      perp = startPerp;
    } else if (i === segments) {
      perp = endPerp;
    } else {
      const tangent = getQuadraticBezierPoint(Math.min(1, t + 0.001), p0, p1, p2).sub(point);
      perp = tangent.lengthSq() > 1e-12
        ? new THREE.Vector2(-tangent.y, tangent.x).normalize()
        : chordPerp.clone();
    }

    left.push(point.clone().addScaledVector(perp, halfWidth));
    right.push(point.clone().addScaledVector(perp, -halfWidth));
  }

  return { left, right };
};

/**
 * Intersect an infinite line (defined by two points on it) with a polyline. The source
 * line parameter is UNRESTRICTED (t can be anywhere on the infinite line) but the polyline
 * segments are treated as finite segments.
 *
 * Used to trim a wall edge against an arc's curved outline where the target edge cannot
 * be represented as a single straight line.
 */
const intersectInfiniteLineWithPolyline = (
  lineP0: THREE.Vector2,
  lineP1: THREE.Vector2,
  polyline: THREE.Vector2[],
): THREE.Vector2[] => {
  const results: THREE.Vector2[] = [];
  const dx = lineP1.x - lineP0.x;
  const dy = lineP1.y - lineP0.y;

  for (let i = 0; i < polyline.length - 1; i++) {
    const s = polyline[i];
    const e = polyline[i + 1];
    const sx = e.x - s.x;
    const sy = e.y - s.y;

    const denom = dx * sy - dy * sx;
    if (Math.abs(denom) < 1e-12) continue;

    const u = ((s.x - lineP0.x) * dy - (s.y - lineP0.y) * dx) / denom;
    if (u < -1e-6 || u > 1 + 1e-6) continue;

    const t = ((s.x - lineP0.x) * sy - (s.y - lineP0.y) * sx) / denom;
    if (!Number.isFinite(t) || !Number.isFinite(u)) continue;

    results.push(new THREE.Vector2(lineP0.x + t * dx, lineP0.y + t * dy));
  }

  return results;
};

export const getEndpointImaginaryIntersections = (
  sourceWall: WallType,
  endpoint: "start" | "end",
  targetWall: WallType,
  fallbackWidth: number = 0.1,
  options?: {
    forceSharedEndpoint?: boolean;
    includeLineWeight?: boolean;
    preferClosestPerSourceSide?: boolean;
  }
): EndpointImaginaryIntersections | null => {
  const {
    forceSharedEndpoint = false,
    includeLineWeight = false,
    preferClosestPerSourceSide = false,
  } = options ?? {};

  const sourceStart = clonePoint(sourceWall.start);
  const sourceEnd = clonePoint(sourceWall.end);
  const targetStart = clonePoint(targetWall.start);
  const targetEnd = clonePoint(targetWall.end);

  const sourceWidth = sourceWall.wallWidth ?? fallbackWidth;
  const targetWidth = targetWall.wallWidth ?? fallbackWidth;

  const connectionPoint = endpoint === "start" ? sourceStart : sourceEnd;
  const targetSharedEndpoint: "start" | "end" | undefined = forceSharedEndpoint
    ? connectionPoint.distanceTo(targetStart) <= connectionPoint.distanceTo(targetEnd) ? "start" : "end"
    : connectionPoint.distanceTo(targetStart) < 1e-3
      ? "start"
      : connectionPoint.distanceTo(targetEnd) < 1e-3
        ? "end"
        : undefined;
  const sourceDirectionAtConnection = getWallDirectionForEndpoint(sourceWall, endpoint);
  const targetDirectionAtConnection = getWallDirectionForEndpoint(targetWall, targetSharedEndpoint);

  // Check if walls are nearly colinear (parallel or anti-parallel).
  // For colinear walls, line intersections go to infinity or are undefined,
  // and we return the wall corner points directly.
  //
  // IMPORTANT: For arc walls, the local tangent at the endpoint can be
  // near-parallel to the other wall's direction even when the arc's overall
  // chord is clearly not colinear (e.g. a perpendicular L-joint with a steep
  // bulge). Using the tangent here would falsely trigger the colinear fallback
  // and produce misaligned corner points, leading to a visible gap on the
  // convex side of the joint. Use the chord direction for the test so that
  // two walls are only treated as colinear when their overall orientations
  // truly are parallel.
  const sourceChordDirection = getWallChordDirection(sourceWall);
  const targetChordDirection = getWallChordDirection(targetWall);
  const localCross = Math.abs(
    sourceChordDirection.x * targetChordDirection.y -
    sourceChordDirection.y * targetChordDirection.x
  );
  if (localCross < Math.sin(0.2)) {
    // For colinear walls, calculate the corner points at the connection endpoint
    const perpendicular = new THREE.Vector2(-sourceDirectionAtConnection.y, sourceDirectionAtConnection.x);
    let halfWidth = sourceWidth / 2;

    // If includeLineWeight is true, extend to the outline edge
    if (includeLineWeight) {
      const { floorplannerStore } = require("../store/floorplannerStore");
      const lineWeight = floorplannerStore.convertLineWeightToWorld(
        sourceWall.lineWeight || floorplannerStore.wallLineWeight
      );
      halfWidth += lineWeight / 2;
    }

    // Return the corner points at the connection endpoint
    const leftCorner = connectionPoint.clone()
      .add(perpendicular.clone().multiplyScalar(halfWidth));
    const rightCorner = connectionPoint.clone()
      .sub(perpendicular.clone().multiplyScalar(halfWidth));

    return {
      left: leftCorner,
      right: rightCorner,
    };
  }

  const sourceLines = calculateWallImaginaryLines(
    sourceWall,
    sourceWidth,
    DEFAULT_EXTENSION,
    includeLineWeight,
    endpoint
  );
  const targetLines = calculateWallImaginaryLines(
    targetWall,
    targetWidth,
    DEFAULT_EXTENSION,
    includeLineWeight,
    targetSharedEndpoint
  );

  // ARC-AWARE middle connection: when the target wall is curved and the source is
  // connecting to somewhere along its body (not at either endpoint), the chord-based
  // target imaginary lines used above bear no resemblance to the wall's actual outline.
  // Intersecting the source edges against those chord lines would place the trim
  // corners on the arc's chord, extending the source wall's fill through the arc all
  // the way to that chord. Instead, intersect the source's edge lines against the
  // ARC's sampled outline polylines and pick the closest intersection to the source's
  // own endpoint corners. That places the corners where the source wall visually meets
  // the curved wall, stopping the fill at the arc's real edge.
  const isArcTarget = targetWall.lineForm === "arc" && !!targetWall.controlPoint;
  const isMiddleConnection = targetSharedEndpoint === undefined && !forceSharedEndpoint;
  if (isArcTarget && isMiddleConnection) {
    let arcHalfWidth = targetWidth / 2;
    if (includeLineWeight) {
      const { floorplannerStore } = require("../store/floorplannerStore");
      const lineWeight = floorplannerStore.convertLineWeightToWorld(
        targetWall.lineWeight || floorplannerStore.wallLineWeight
      );
      arcHalfWidth += lineWeight / 2;
    }

    const arcOutlines = sampleArcOutlinePolylines(targetWall, arcHalfWidth);
    if (arcOutlines) {
      // Source-side reference corners: where the source wall's two edges terminate at
      // the connecting endpoint. Used as a tie-breaker when picking between multiple
      // intersections on the same polyline.
      const sourcePerpendicular = new THREE.Vector2(
        -sourceDirectionAtConnection.y,
        sourceDirectionAtConnection.x
      );
      let sourceHalfWidth = sourceWidth / 2;
      if (includeLineWeight) {
        const { floorplannerStore } = require("../store/floorplannerStore");
        const lineWeight = floorplannerStore.convertLineWeightToWorld(
          sourceWall.lineWeight || floorplannerStore.wallLineWeight
        );
        sourceHalfWidth += lineWeight / 2;
      }
      const sourceLeftCorner = connectionPoint.clone()
        .addScaledVector(sourcePerpendicular, sourceHalfWidth);
      const sourceRightCorner = connectionPoint.clone()
        .addScaledVector(sourcePerpendicular, -sourceHalfWidth);

      // Determine which arc polyline the source wall approaches from.
      //
      // Picking the intersection point closest to the source corner is NOT robust:
      // when the connection is off-center on the arc, the source wall's corners can
      // fall INSIDE the arc's thickness and end up closer to the far (inner) outline
      // than the approach (outer) outline. That produces corner points on the wrong
      // side of the arc, which in turn places the outline gap on the wrong side and
      // leaves the source wall visually extending through the arc.
      //
      // Instead, use the source wall's far endpoint (the endpoint opposite the
      // connection) as a reference. It is guaranteed to lie clearly outside the arc
      // on the approach side, so whichever outline polyline it is closer to is the
      // side the source wall is actually approaching from.
      const minDistToPolyline = (point: THREE.Vector2, polyline: THREE.Vector2[]): number => {
        let best = Infinity;
        for (let i = 0; i < polyline.length - 1; i++) {
          const a = polyline[i];
          const b = polyline[i + 1];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const lenSq = dx * dx + dy * dy;
          if (lenSq < 1e-12) continue;
          let u = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq;
          if (u < 0) u = 0;
          else if (u > 1) u = 1;
          const px = a.x + u * dx;
          const py = a.y + u * dy;
          const d = Math.hypot(point.x - px, point.y - py);
          if (d < best) best = d;
        }
        return best;
      };

      const sourceFarEndpoint = endpoint === "start" ? sourceEnd : sourceStart;
      const approachSide = minDistToPolyline(sourceFarEndpoint, arcOutlines.left)
        <= minDistToPolyline(sourceFarEndpoint, arcOutlines.right)
        ? arcOutlines.left
        : arcOutlines.right;

      const pickOnApproachSide = (
        line: ImaginaryLineSegment,
        reference: THREE.Vector2,
      ): THREE.Vector2 | null => {
        const candidates = intersectInfiniteLineWithPolyline(line.start, line.end, approachSide);
        if (candidates.length === 0) return null;
        candidates.sort((a, b) => a.distanceToSquared(reference) - b.distanceToSquared(reference));
        return candidates[0].clone();
      };

      const leftOnArc = pickOnApproachSide(sourceLines.left, sourceLeftCorner);
      const rightOnArc = pickOnApproachSide(sourceLines.right, sourceRightCorner);

      if (leftOnArc && rightOnArc) {
        return { left: leftOnArc, right: rightOnArc };
      }
      // If we couldn't find intersections on the curved outline (e.g. geometry is
      // degenerate or the source wall is completely outside the arc), fall through
      // to the straight-line approximation below so we still produce something
      // sensible rather than nothing at all.
    }
  }

  const sharesEndpointWithTarget = forceSharedEndpoint || targetSharedEndpoint !== undefined;
  const sourceCenter = sourceStart.clone().add(sourceEnd).multiplyScalar(0.5);
  const targetCenter = targetStart.clone().add(targetEnd).multiplyScalar(0.5);

  const getInsideVector = (): THREE.Vector2 => {
    const insideCandidate = new THREE.Vector2()
      .addVectors(sourceCenter, targetCenter)
      .multiplyScalar(0.5)
      .sub(connectionPoint);

    if (insideCandidate.lengthSq() > 1e-8) {
      return insideCandidate;
    }

    const toTarget = targetCenter.clone().sub(connectionPoint);
    if (toTarget.lengthSq() > 1e-8) {
      return toTarget;
    }

    const toSource = sourceCenter.clone().sub(connectionPoint);
    if (toSource.lengthSq() > 1e-8) {
      return toSource;
    }

    const fallback = new THREE.Vector2()
      .addVectors(
        new THREE.Vector2().subVectors(sourceEnd, sourceStart),
        new THREE.Vector2().subVectors(targetEnd, targetStart),
      );
    if (fallback.lengthSq() > 1e-8) {
      return fallback;
    }

    return new THREE.Vector2(1, 0);
  };

  const insideVector = getInsideVector();

  const computeInsideSide = (
    wall: WallType,
    vector: THREE.Vector2,
    connectionEndpoint?: "start" | "end",
  ): "left" | "right" => {
    const direction = getWallDirectionForEndpoint(wall, connectionEndpoint);
    if (direction.lengthSq() === 0) {
      return "left";
    }
    const cross = direction.x * vector.y - direction.y * vector.x;
    return cross >= 0 ? "left" : "right";
  };

  const sourceInsideSide = computeInsideSide(sourceWall, insideVector, endpoint);
  const targetInsideSide = computeInsideSide(targetWall, insideVector, targetSharedEndpoint);
  const sourceOutsideSide = sourceInsideSide === "left" ? "right" : "left";
  const targetOutsideSide = targetInsideSide === "left" ? "right" : "left";

  const getIntersectionForSides = (
    sourceSide: "left" | "right",
    targetSide: "left" | "right",
  ): THREE.Vector2 | null => {
    const sourceLine = sourceSide === "left" ? sourceLines.left : sourceLines.right;
    const targetLine = targetSide === "left" ? targetLines.left : targetLines.right;
    const intersection = intersectImaginaryLines(sourceLine, targetLine);
    if (!intersection) {
      return null;
    }
    return intersection.point.clone();
  };

  if (preferClosestPerSourceSide) {
    const sourcePerpendicular = new THREE.Vector2(
      -sourceDirectionAtConnection.y,
      sourceDirectionAtConnection.x
    );
    let sourceHalfWidth = sourceWidth / 2;
    if (includeLineWeight) {
      const { floorplannerStore } = require("../store/floorplannerStore");
      const lineWeight = floorplannerStore.convertLineWeightToWorld(
        sourceWall.lineWeight || floorplannerStore.wallLineWeight
      );
      sourceHalfWidth += lineWeight / 2;
    }
    const sourceLeftCorner = connectionPoint.clone()
      .add(sourcePerpendicular.clone().multiplyScalar(sourceHalfWidth));
    const sourceRightCorner = connectionPoint.clone()
      .sub(sourcePerpendicular.clone().multiplyScalar(sourceHalfWidth));

    const pickClosestPointToSourceCorner = (
      sourceLine: ImaginaryLineSegment,
      sourceCorner: THREE.Vector2,
    ): THREE.Vector2 | null => {
      const candidates = [targetLines.left, targetLines.right]
        .map(line => intersectImaginaryLines(sourceLine, line))
        .filter((intersection): intersection is ImaginaryLineIntersection => Boolean(intersection))
        .map(intersection => ({
          point: intersection.point.clone(),
          dist: intersection.point.distanceToSquared(sourceCorner),
        }));

      if (candidates.length === 0) {
        return null;
      }

      candidates.sort((a, b) => a.dist - b.dist);
      return candidates[0].point;
    };

    const leftClosest = pickClosestPointToSourceCorner(sourceLines.left, sourceLeftCorner);
    const rightClosest = pickClosestPointToSourceCorner(sourceLines.right, sourceRightCorner);

    if (leftClosest && rightClosest) {
      return {
        left: leftClosest,
        right: rightClosest,
      };
    }
  }

  const insideIntersection = getIntersectionForSides(sourceInsideSide, targetInsideSide);
  const outsideIntersection = getIntersectionForSides(sourceOutsideSide, targetOutsideSide);

  if (sharesEndpointWithTarget && insideIntersection && outsideIntersection) {
    const leftPoint =
      sourceInsideSide === "left" ? insideIntersection : outsideIntersection;
    const rightPoint =
      sourceInsideSide === "left" ? outsideIntersection : insideIntersection;

    return {
      left: leftPoint,
      right: rightPoint,
    };
  }

  const referencePoint = endpoint === "start" ? sourceWall.end : sourceWall.start;

  const pickClosestPoint = (sourceLine: ImaginaryLineSegment): THREE.Vector2 | null => {
    const candidates = [targetLines.left, targetLines.right]
      .map(line => intersectImaginaryLines(sourceLine, line))
      .filter((intersection): intersection is ImaginaryLineIntersection => Boolean(intersection))
      .map(intersection => ({
        point: intersection.point.clone(),
        dist: intersection.point.distanceToSquared(referencePoint),
      }));

    if (candidates.length === 0) {
      return null;
    }

    candidates.sort((a, b) => a.dist - b.dist);
    return candidates[0].point;
  };

  const leftPoint = pickClosestPoint(sourceLines.left);
  const rightPoint = pickClosestPoint(sourceLines.right);

  if (!leftPoint || !rightPoint) {
    return null;
  }

  return {
    left: leftPoint,
    right: rightPoint,
  };
};
